// Who's here (W9b, PROTOCOL.md §10.12): the open project's presence — this device's caret on its way out, the other
// members' as they arrive — over the project's own socket (LiveProject's `sendPresence`/`onPresenceFrame`).
//
// - This device: the editor (store/cowriting.ts) reports the caret's fragment and selection, which go as scalars at the
//   fragment's heads, at most 4 frames a second, with a heartbeat every 15 s, `typing` for 2 s after an edit, and
//   `gone` when the page is hidden, idle for 3 minutes, or closed (PresenceSender, Web/client/src/net/presence.ts).
//   Frames are sealed in order (a `gone` never overtakes the state before it).
// - The others: each frame opened with the project key and checked against the sender the relay names, kept until its
//   TTL or their `gone` (PresenceRoster); a lost connection forgets them until it's back.
// - While someone is in a fragment, this device's changes to it are pushed after 150 ms instead of 2 s
//   (`Outbox.setQuick`).
// - Names and colours come from the project's `members` map (W9c) when it has the member, else the name their frames
//   carry and a colour picked from EntityColors by their account id (`presenceIdentity`).
import { useSyncExternalStore } from 'react';
import type * as A from '@automerge/automerge';
import { parseAddress } from '@writing-suite/client/net/address.ts';
import {
  openPresence, PRESENCE_TTL_MS, presenceAAD, PresenceRoster, PresenceSender, resolveSelection, sealPresence, selectionAt,
  type PresenceEntry, type PresencePayload, type PresenceState,
} from '@writing-suite/client/net/presence.ts';
import { ENTITY_HUES, entityColorString, rgbToCSS } from '@writing-suite/core';
import { hasMembers, readMembers, type Members } from '@writing-suite/core/members';
import type { LiveProject } from './project.ts';

export interface PresencePerson {
  account: string;
  device: string;
  name: string;
  /** CSS colour. */
  color: string;
  fragmentId: string | null;
  typing: boolean;
}

export interface PresenceSnapshot {
  /** The other members here now, one per account (their most recent device). */
  others: PresencePerson[];
  /** The fragment this device's caret is in. */
  mine: string | null;
}

const EMPTY: PresenceSnapshot = { others: [], mine: null };

const membersCache = new WeakMap<A.Doc<unknown>, Members>();

function hashOf(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** A member's colour when the project has none for them: one of the twelve entity hues, by account id. */
export const fallbackColor = (accountId: string) => rgbToCSS(entityColorString(ENTITY_HUES[hashOf(accountId) % ENTITY_HUES.length]))!;

/**
 * A member's name and colour (CSS): the project's `members` map (W9c) when it has them, else `fallbackName` (what their
 * frames say they're called) and a colour from EntityColors by account id.
 */
export function presenceIdentity(project: A.Doc<unknown> | null | undefined, accountId: string, fallbackName?: string | null): { name: string; color: string } {
  let member: Members['list'][number] | undefined;
  if (project) {
    try {
      let members = membersCache.get(project);
      if (!members) membersCache.set(project, (members = readMembers(project)));
      member = members.byId.get(accountId);
    } catch {
      member = undefined;
    }
  }
  const named = member?.name && member.name !== 'Someone' ? member.name : null;
  return {
    name: named ?? (fallbackName?.trim() || member?.name || 'A co-writer'),
    color: (member?.color ? rgbToCSS(member.color) : null) ?? fallbackColor(accountId),
  };
}

export interface Me {
  account: string;
  device: string;
  /** The account's name as the frames carry it (read when each frame goes). */
  name(): string | undefined;
}

export class ProjectPresence {
  readonly roster: PresenceRoster;
  private readonly sender: PresenceSender;
  private readonly aad: string;
  private seq = 0;
  /** Seals and sends in order. */
  private chain: Promise<unknown> = Promise.resolve();
  private readonly offs: (() => void)[] = [];
  private listeners = new Set<() => void>();
  private snapshot: PresenceSnapshot = EMPTY;
  private mine: string | null = null;
  private stopped = false;

  constructor(readonly live: LiveProject, private readonly me: Me) {
    const address = parseAddress(live.id);
    this.aad = presenceAAD(address?.owner ?? me.account, address?.projectId ?? live.id);
    this.roster = new PresenceRoster({ self: me.account });
    this.sender = new PresenceSender({ send: (state) => this.send(state) });
    this.offs.push(live.onPresenceFrame((header, body) => void this.receive(header, body)));
    let connection = live.state.connection;
    this.offs.push(
      live.subscribe((s) => {
        if (s.connection === connection) return;
        connection = s.connection;
        if (s.connection === 'live') this.sender.resend();
        else this.roster.clear();
      }),
    );
    this.offs.push(this.roster.subscribe(() => this.changed()));
    // The project document changed (a member's name or colour), or a fragment someone is in: their caret again.
    this.offs.push(live.onRemote((doc) => {
      if (doc === 'project' || this.roster.entries().some((e) => `fragment.${e.payload.fragment}` === doc)) this.changed();
    }));
    if (typeof document !== 'undefined') {
      const visibility = () => this.sender.setVisible(document.visibilityState !== 'hidden');
      const activity = () => this.sender.activity();
      document.addEventListener('visibilitychange', visibility);
      window.addEventListener('keydown', activity, { capture: true, passive: true });
      window.addEventListener('pointerdown', activity, { capture: true, passive: true });
      this.offs.push(() => {
        document.removeEventListener('visibilitychange', visibility);
        window.removeEventListener('keydown', activity, { capture: true });
        window.removeEventListener('pointerdown', activity, { capture: true });
      });
      visibility();
    }
    this.sender.update({ fragment: null });
  }

  // MARK: This device

  /** The editor's caret moved (UTF-16 in its fragment), or it has none (another room, a divider). Cheap: it's called
   *  with every keystroke, and the positions are worked out only when a frame goes (`send`). */
  caret(sel: { fragmentId: string; anchor: number; head: number } | null) {
    if (this.stopped) return;
    this.selection = sel;
    const fragment = sel?.fragmentId ?? null;
    if (fragment !== this.mine) {
      this.mine = fragment;
      this.changed();
    }
    this.sender.update({ fragment });
  }

  /** The caret as the editor last reported it. */
  private selection: { fragmentId: string; anchor: number; head: number } | null = null;

  /** The writer typed (their flag says "writing" for a moment). */
  typed() {
    if (!this.stopped) this.sender.typed();
  }

  /** Whether anyone else could be here: the project is shared with this account, or its owner has shared it (W9c's
   *  `members`). A project of one's own that isn't shared sends nothing (each frame wakes the project's object). */
  private shared(): boolean {
    if (this.live.role !== 'owner') return true;
    const project = this.live.doc('project');
    if (!project) return false;
    if (this.sharedFor.doc !== project) this.sharedFor = { doc: project, value: hasMembers(project) };
    return this.sharedFor.value;
  }
  private sharedFor: { doc: A.Doc<unknown> | null; value: boolean } = { doc: null, value: false };

  /** Whether this device's presence goes anywhere (the project is shared): the editor reports nothing otherwise. */
  get active(): boolean {
    return !this.stopped && this.shared();
  }

  private send(state: (PresenceState & { typing: boolean }) | null): boolean {
    if (this.live.state.connection !== 'live' || !this.shared()) return false;
    if (!state) {
      this.chain = this.chain.then(() => this.live.sendPresence({ gone: true }));
      return true;
    }
    // The selection as scalars of the fragment's text now, at its heads (the editor and the document are in step).
    const sel = this.selection && this.selection.fragmentId === state.fragment ? this.selection : null;
    const doc = sel ? this.live.doc(`fragment.${sel.fragmentId}`) : undefined;
    const at = sel && doc ? selectionAt(doc, sel.anchor, sel.head) : null;
    const payload: PresencePayload = {
      v: 1, member: this.me.account, device: this.me.device, seq: ++this.seq, fragment: state.fragment, typing: state.typing,
      ...(this.me.name()?.trim() ? { name: this.me.name()!.trim().slice(0, 80) } : {}),
      ...(at ?? {}),
    };
    const key = this.live.key;
    const sealing = sealPresence(key, this.aad, payload);
    this.chain = this.chain.then(async () => this.live.sendPresence({ ttl: PRESENCE_TTL_MS, sealed: await sealing })).catch(() => {});
    return true;
  }

  // MARK: The others

  private async receive(header: { from: { account: string; device: string }; ttl?: number; gone?: boolean }, body: Uint8Array) {
    if (this.stopped) return;
    if (header.gone) return this.roster.gone(header.from);
    const payload = await openPresence(this.live.key, this.aad, body, header.from);
    if (!payload || this.stopped) return;
    // A device we hadn't heard from (one that just opened the project): say where we are now, so it doesn't wait for
    // our next heartbeat (up to 15 s) to see us. It hears from us once; we were new to it too, so it answers once.
    const known = this.roster.entries().some((e) => e.device === payload.device);
    if (this.roster.put(payload, header.ttl ?? PRESENCE_TTL_MS) && !known) this.sender.resend();
  }

  private person(e: PresenceEntry): PresencePerson {
    const id = presenceIdentity(this.live.doc('project'), e.account, e.payload.name);
    return { account: e.account, device: e.device, name: id.name, color: id.color, fragmentId: e.payload.fragment, typing: e.payload.typing };
  }

  /**
   * The others' carets for an editor showing `fragmentIds`: resolved to UTF-16 offsets of the text now (`set`), or —
   * when the text they were made in hasn't arrived yet — `keep` (left where the editor has carried them).
   */
  carets(fragmentIds: ReadonlySet<string>): { set: { key: string; name: string; color: string; fragmentId: string; anchor: number; head: number; typing: boolean }[]; keep: string[]; update: { key: string; name: string; color: string; typing: boolean }[] } {
    const set: ReturnType<ProjectPresence['carets']>['set'] = [];
    const keep: string[] = [], update: ReturnType<ProjectPresence['carets']>['update'] = [];
    for (const e of this.roster.entries()) {
      const fid = e.payload.fragment;
      if (!fid || !fragmentIds.has(fid)) continue;
      const p = this.person(e);
      const doc = this.live.doc(`fragment.${fid}`);
      const at = doc ? resolveSelection(doc, e.payload) : null;
      if (at) set.push({ key: e.device, name: p.name, color: p.color, fragmentId: fid, anchor: at.anchor, head: at.head, typing: p.typing });
      else {
        keep.push(e.device);
        update.push({ key: e.device, name: p.name, color: p.color, typing: p.typing });
      }
    }
    return { set, keep, update };
  }

  // MARK: Following it

  private changed() {
    if (this.stopped) return;
    const others = this.roster.perAccount().map((e) => this.person(e));
    this.snapshot = { others, mine: this.mine };
    // Someone in a fragment: this device's changes to it go quickly.
    this.live.outbox?.setQuick(this.roster.entries().flatMap((e) => (e.payload.fragment ? [`fragment.${e.payload.fragment}`] : [])));
    for (const fn of [...this.listeners]) fn();
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  get state(): PresenceSnapshot {
    return this.snapshot;
  }

  /** Closing the project: `gone`, and nothing more. */
  stop() {
    if (this.stopped) return;
    this.sender.stop();
    this.stopped = true;
    this.roster.stop();
    for (const off of this.offs) off();
    this.listeners.clear();
    this.live.outbox?.setQuick([]);
    if (current === this) setCurrent(null);
  }
}

// MARK: The open project's presence

const byLive = new WeakMap<LiveProject, ProjectPresence>();
let current: ProjectPresence | null = null;
const currentListeners = new Set<() => void>();
function setCurrent(p: ProjectPresence | null) {
  current = p;
  for (const fn of [...currentListeners]) fn();
}

/** Starts presence for a live project (the open one), stopped with `stop` when it closes. */
export function startPresence(live: LiveProject, me: Me): ProjectPresence | null {
  if (!live.live) return null;
  const p = new ProjectPresence(live, me);
  byLive.set(live, p);
  setCurrent(p);
  return p;
}

/** A live project's presence, if it was started. */
export const presenceOf = (live: LiveProject | null | undefined): ProjectPresence | null => (live ? byLive.get(live) ?? null : null);

function usePresenceObject(): ProjectPresence | null {
  return useSyncExternalStore(
    (fn) => {
      currentListeners.add(fn);
      return () => currentListeners.delete(fn);
    },
    () => current,
  );
}

/** Who's here in the open project, following it. */
export function usePresence(): PresenceSnapshot {
  const p = usePresenceObject();
  return useSyncExternalStore((fn) => p?.subscribe(fn) ?? (() => {}), () => p?.state ?? EMPTY);
}
